import { useCallback, useState } from 'react'
import { isBefore, isSameDay } from 'date-fns'
import { TDateRange } from '@pinchenterprisesnpm/friday-ui'

type TUseDatePickerRangeReturnType = [
  TDateRange,
  () => void,
  (_: any, day: Date) => void,
  () => void,
  (presetDateRange: TDateRange) => void
]

export const emptyDateRange: TDateRange = {
  from: undefined,
  to: undefined,
}

export const isBeforeToday = (day: Date) => {
  const today = new Date()
  return isBefore(day, today) && !isSameDay(day, today)
}

/**
 * Function that checks whether the user is selecting a day that should be the first day in the date range
 * @param selectedDateRange - selected date range before new day selection
 * @param selectedDay - the day user selected
 * @returns boolean
 */
export const isSelectingFirstDay = (selectedDateRange: TDateRange, selectedDay?: Date) => {
  const { from, to } = selectedDateRange
  const isBeforeFirstDay = !!(from && isBefore(selectedDay as Date, from))
  const isRangeSelected = !!(from && to)

  return !from || isBeforeFirstDay || isRangeSelected
}

/**
 * Function for getting new selected date range depending on the last range selection and new selected day.
 * We are modifying the default behavior of selection in range date picker from react-day-picker.
 * @param selectedDateRange - selected date range before new day selection
 * @param selectedDay - the day user selected
 * @returns TDateRange - new selected date range
 */
export const getNewSelectedDateRange = (selectedDateRange: TDateRange, selectedDay: Date) => {
  if (isSelectingFirstDay(selectedDateRange, selectedDay)) {
    return { from: selectedDay, to: undefined }
  }
  return { from: selectedDateRange.from, to: selectedDay }
}

/**
 * Hook that handles and exposes data and utils for the DatePickerRange component
 * @param initialDateRange Specify the initial date range. Optional.
 */
export const useDatePickerRange = (
  initialDateRange?: TDateRange
): TUseDatePickerRangeReturnType => {
  const [selectedDateRange, setSelectedDateRange] = useState<TDateRange>(
    initialDateRange || emptyDateRange
  )

  const setFirstDay = useCallback((day: Date) => {
    setSelectedDateRange({ to: undefined, from: day })
  }, [])

  const setLastDay = useCallback((day: Date) => {
    setSelectedDateRange((dateRange) => {
      return { to: day, from: dateRange.from }
    })
  }, [])

  const onReset = useCallback(() => {
    setSelectedDateRange(emptyDateRange)
  }, [])

  const onSelectRange = useCallback(
    (_: unknown, day: Date) => {
      if (isSelectingFirstDay(selectedDateRange, day)) setFirstDay(day)
      else setLastDay(day)
    },
    [selectedDateRange, setFirstDay, setLastDay]
  )

  const handleSelectPresetDates = useCallback((presetDateRange: TDateRange) => {
    setSelectedDateRange(presetDateRange)
  }, [])

  const onClose = useCallback(() => {
    const { from, to } = selectedDateRange
    if (from && !to) setLastDay(from)
  }, [selectedDateRange, setLastDay])

  return [selectedDateRange, onReset, onSelectRange, onClose, handleSelectPresetDates]
}
